/**
 * THE way a date or time is shown to a person, everywhere in the app.
 *
 *   datetime (default)  November 6, 2026 · 11:30 AM (IST)
 *   date                November 6, 2026
 *   time                11:30 AM (IST)
 *   with `end`          November 6, 2026 · 11:30 AM - 12:30 PM (IST)
 *                       November 6, 2026 · 11:30 PM - November 7, 2026 · 12:30 AM (IST)
 *
 * The month is spelled out everywhere by default — one standard across the
 * app. `length: "short"` (Nov 6, 2026) exists only for a place that genuinely
 * cannot fit the full name.
 *
 * Shown in the viewer's own timezone unless `timeZone` names another (e.g. an
 * event that is fixed to its venue's zone). The zone is always labelled on
 * anything with a time, so a reader never has to guess whose 11:30 it is.
 *
 * NOT for machine values — API payloads (`yyyy-MM-dd`), <input> values
 * (`HH:mm`), calendar-grid cells — and not for relative stamps ("5m ago"),
 * which live in relativeTime.ts.
 *
 * PARSING
 * -------
 * Most backend timestamps come from timezone-NAIVE columns that hold UTC: the
 * instant is UTC but the marker is stripped, so `new Date("2026-11-06T06:00")`
 * reads it as LOCAL time and shifts it by the reader's offset. An ISO datetime
 * with no offset is therefore read as UTC here. A bare `yyyy-MM-dd` is a
 * calendar date, not an instant, and is shown as that same date in every zone.
 */

import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export type DateInput = Date | string | number | null | undefined;

export type DisplayDateVariant = "datetime" | "date" | "time";

export type DisplayDateLength = "short" | "long";

export interface FormatDisplayDateOptions {
  /** What to show. Defaults to "datetime". */
  variant?: DisplayDateVariant;
  /** "long" (default) spells the month out (November); "short" abbreviates it (Nov). */
  length?: DisplayDateLength;
  /** End of a range (a session's end time). Ignored for the "date" variant. */
  end?: DateInput;
  /** IANA zone to show the time in. Defaults to the viewer's browser zone. */
  timeZone?: string;
  /** Returned when the value is missing or unparseable. Defaults to "". */
  fallback?: string;
}

const DATE_PATTERNS: Record<DisplayDateLength, string> = { short: "MMM d, yyyy", long: "MMMM d, yyyy" };
const TIME_PATTERN = "h:mm a";
const DATE_TIME_SEPARATOR = " · ";
const RANGE_SEPARATOR = " - ";
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_OFFSET = /([Zz]|[+-]\d{2}:?\d{2})$/;

// `Intl` only knows an abbreviation like "IST" under the locale that uses it:
// en-US gives "GMT+5:30" for Asia/Kolkata, en-IN gives "IST". The first locale
// that yields a real abbreviation wins; otherwise the GMT offset is shown.
const ABBREVIATION_LOCALES = ["en-US", "en-IN", "en-GB", "en-AU"];
const OFFSET_STYLE = /^(GMT|UTC)[+-]/;

const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const zoneAbbreviation = (instant: Date, timeZone: string): string => {
  let offsetStyle = "";
  for (const locale of ABBREVIATION_LOCALES) {
    try {
      const name = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
        .formatToParts(instant)
        .find((part) => part.type === "timeZoneName")?.value;
      if (!name) continue;
      if (!OFFSET_STYLE.test(name)) return name;
      offsetStyle ||= name;
    } catch {
      // Unknown zone in this runtime: fall through to the offset form.
    }
  }
  return offsetStyle || timeZone;
};

type Parsed = { instant: Date; dateOnly: boolean };

const parse = (value: DateInput): Parsed | null => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isValid(value) ? { instant: value, dateOnly: false } : null;
  if (typeof value === "number") {
    const instant = new Date(value);
    return isValid(instant) ? { instant, dateOnly: false } : null;
  }
  const raw = value.trim();
  if (DATE_ONLY.test(raw)) {
    // Noon UTC keeps the calendar date the same in every zone from -12 to +11.
    const instant = parseISO(`${raw}T12:00:00Z`);
    return isValid(instant) ? { instant, dateOnly: true } : null;
  }
  const instant = parseISO(HAS_OFFSET.test(raw) ? raw : `${raw}Z`);
  return isValid(instant) ? { instant, dateOnly: false } : null;
};

/**
 * The instant behind a value, parsed by the same rules as formatDisplayDate.
 *
 * Only for visual fragments that are not a readable date — a calendar chip's
 * big day number, a month grid — and for comparisons. Any date a person reads
 * as text goes through formatDisplayDate.
 */
export function parseDateInput(value: DateInput): Date | null {
  return parse(value)?.instant ?? null;
}

export function formatDisplayDate(value: DateInput, options: FormatDisplayDateOptions = {}): string {
  const { variant = "datetime", length = "long", end, fallback = "" } = options;
  const datePattern = DATE_PATTERNS[length];
  const start = parse(value);
  if (!start) return fallback;

  // A calendar date has no time and no zone; show it as written.
  if (start.dateOnly) return formatInTimeZone(start.instant, "UTC", datePattern);

  const timeZone = options.timeZone || viewerTimeZone();
  const date = (d: Date) => formatInTimeZone(d, timeZone, datePattern);
  const time = (d: Date) => formatInTimeZone(d, timeZone, TIME_PATTERN);
  const dateTime = (d: Date) => `${date(d)}${DATE_TIME_SEPARATOR}${time(d)}`;
  const zone = ` (${zoneAbbreviation(start.instant, timeZone)})`;

  if (variant === "date") return date(start.instant);

  const finish = end !== undefined ? parse(end) : null;
  const head = variant === "time" ? time(start.instant) : dateTime(start.instant);
  if (!finish || finish.dateOnly) return `${head}${zone}`;

  const sameDay = date(start.instant) === date(finish.instant);
  const tail = sameDay ? time(finish.instant) : dateTime(finish.instant);
  return `${head}${RANGE_SEPARATOR}${tail}${zone}`;
}
